import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useInstallPreferences } from '@/hooks/useInstallPreferences';
import { useInstallTracking } from '@/hooks/useInstallTracking';
import { useTranslation } from 'react-i18next';

const InstallModal = () => {
  const { t } = useTranslation();
  const { isInstallable, isInstalled, promptInstall } = useInstallPrompt();
  const { preferences, markModalShown } = useInstallPreferences();
  const { track } = useInstallTracking();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (preferences.modalShown || isInstalled || !isInstallable) return;
    const timer = setTimeout(() => {
      setOpen(true);
      markModalShown();
      track('modal_shown');
    }, 5000);
    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled, preferences.modalShown, markModalShown, track]);

  const handleInstall = async () => {
    track('modal_installed');
    const accepted = await promptInstall();
    if (accepted) track('install_success', { source: 'modal' });
    setOpen(false);
  };

  const handleDismiss = () => {
    track('modal_dismissed');
    setOpen(false);
  };

  const benefits = [
    t('install.modal.benefits.0', 'Aplicar para projetos em segundos'),
    t('install.modal.benefits.1', 'Ter fácil acesso no seu celular'),
    t('install.modal.benefits.2', 'Funcionar offline'),
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleDismiss()}>
      <DialogContent className="bg-gradient-to-br from-card to-background border-2 border-primary sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold text-primary">
            {t('install.modal.welcome', 'Bem-vindo ao Brooklyn Studio! 👑')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-center text-foreground font-medium">
            {t('install.modal.title', 'Instale nosso app para:')}
          </p>
          
          <ul className="space-y-3">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-primary shrink-0" />
                <span className="text-foreground text-sm">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleInstall}
            className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:opacity-90 font-bold h-12"
          >
            {t('install.modal.installNow', 'Instalar Agora')}
          </Button>
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="text-muted-foreground"
          >
            {t('install.modal.maybeLater', 'Talvez Depois')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallModal;
